// Seller Hub data — metrics, tasks, and orders list for the seller-hub page.
// All queries are scoped to the signed-in seller via RLS.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type SellerHubMetrics = {
  activeOffers: number; // offers with status = pending
  awaitingPhotos: number; // accepted raw offers still needing photos (pending_buyer_confirmation)
  awaitingShipment: number; // txn funds_held (paid, not shipped)
  completedThisMonth: number; // released/delivered txns in current month
  totalOffersSubmitted: number; // lifetime count (used for nav gating too)
};

export type SellerHubTask = {
  id: string;
  kind: "submit_photos" | "ship_now" | "await_payment" | "leave_rating";
  title: string;
  body: string;
  link: string;
  urgencyRank: number; // lower = more urgent
  deadlineAt: string | null;
};

export type SellerHubSalesPoint = { date: string; cents: number };

export type SellerHubOrderRow = {
  offerId: string;
  buyOrderId: string;
  transactionId: string | null;
  cardName: string | null;
  buyerId: string | null;
  buyerUsername: string | null;
  buyerRatingAvg: number | null;
  priceCents: number;
  shippingCents: number;
  currency: string;
  offerStatus: string;
  txnStatus: string | null;
  updatedAt: string;
  createdAt: string;
  bucket:
    | "pending_review"
    | "photo_submission"
    | "awaiting_payment"
    | "payment_confirmed"
    | "shipped"
    | "completed"
    | "withdrawn";
};

export type SellerHubOverview = {
  metrics: SellerHubMetrics;
  tasks: SellerHubTask[];
  sales31d: SellerHubSalesPoint[];
  earningsThisMonthCents: number;
  earningsLastMonthCents: number;
  avgOrderCents: number;
  feedback: {
    ratingAvg: number;
    ratingCount: number;
    recent30d: number;
  };
};

function classifyBucket(offerStatus: string, txnStatus: string | null): SellerHubOrderRow["bucket"] {
  if (txnStatus === "released" || txnStatus === "delivered") return "completed";
  if (txnStatus === "shipped") return "shipped";
  if (txnStatus === "funds_held") return "payment_confirmed";
  if (txnStatus === "pending_payment") return "awaiting_payment";
  if (offerStatus === "pending_buyer_confirmation") return "photo_submission";
  if (offerStatus === "pending") return "pending_review";
  return "withdrawn";
}

export const getSellerHubOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SellerHubOverview> => {
    const { supabase, userId } = context;
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
    const thirtyOneAgo = new Date(now.getTime() - 31 * 24 * 3600 * 1000).toISOString();
    const thirtyAgo = new Date(now.getTime() - 30 * 24 * 3600 * 1000).toISOString();

    const [offersRes, txnsRes, profileRes] = await Promise.all([
      supabase
        .from("offers")
        .select("id, status, price_cents, currency, buy_order_id, buyer_confirmation_deadline, created_at, buy_orders(card_name, title)")
        .eq("seller_id", userId)
        .order("created_at", { ascending: false }),
      supabase
        .from("transactions")
        .select("id, offer_id, buy_order_id, status, amount_cents, shipped_at, released_at, delivered_at, created_at, buy_orders(card_name, title)")
        .eq("seller_id", userId),
      supabase
        .from("profiles")
        .select("seller_rating_avg, seller_rating_count")
        .eq("id", userId)
        .maybeSingle(),
    ]);

    const offers = offersRes.data ?? [];
    const txns = txnsRes.data ?? [];
    const txnByOffer = new Map<string, (typeof txns)[number]>();
    for (const t of txns) if (t.offer_id) txnByOffer.set(t.offer_id, t);

    // Metrics
    let activeOffers = 0;
    let awaitingPhotos = 0;
    let awaitingShipment = 0;
    let completedThisMonth = 0;

    for (const o of offers) {
      if (o.status === "pending") activeOffers++;
      if (o.status === "pending_buyer_confirmation") awaitingPhotos++;
    }
    for (const t of txns) {
      if (t.status === "funds_held") awaitingShipment++;
      if (
        (t.status === "released" || t.status === "delivered") &&
        (t.released_at ?? t.delivered_at ?? t.created_at) >= monthStart
      ) {
        completedThisMonth++;
      }
    }

    // Tasks
    const tasks: SellerHubTask[] = [];
    for (const o of offers) {
      const bo = (o as { buy_orders?: { card_name?: string | null; title?: string | null } }).buy_orders;
      const label = bo?.card_name ?? bo?.title ?? "your order";
      if (o.status === "pending_buyer_confirmation" && o.buyer_confirmation_deadline) {
        const hoursLeft = Math.max(0, Math.round((new Date(o.buyer_confirmation_deadline).getTime() - now.getTime()) / 3_600_000));
        tasks.push({
          id: `photos-${o.id}`,
          kind: "submit_photos",
          title: `Awaiting buyer photo review — ${label}`,
          body: `Buyer has ${hoursLeft}h remaining to confirm your photos.`,
          link: `/orders/${o.buy_order_id}`,
          urgencyRank: hoursLeft,
          deadlineAt: o.buyer_confirmation_deadline,
        });
      }
    }
    for (const t of txns) {
      const bo = (t as { buy_orders?: { card_name?: string | null; title?: string | null } }).buy_orders;
      const label = bo?.card_name ?? bo?.title ?? "your order";
      if (t.status === "funds_held") {
        tasks.push({
          id: `ship-${t.id}`,
          kind: "ship_now",
          title: `Payment confirmed — ship ${label} now`,
          body: `Payment has cleared. Ship the item and mark it shipped.`,
          link: `/orders/${t.buy_order_id}`,
          urgencyRank: 0,
          deadlineAt: null,
        });
      } else if (t.status === "pending_payment") {
        tasks.push({
          id: `await-${t.id}`,
          kind: "await_payment",
          title: `Awaiting buyer payment — ${label}`,
          body: `Buyer approved your photos. Await payment from buyer, then ship — no seller payment action needed.`,
          link: `/orders/${t.buy_order_id}`,
          urgencyRank: 5,
          deadlineAt: null,
        });
      } else if (t.status === "delivered") {
        tasks.push({
          id: `rate-${t.id}`,
          kind: "leave_rating",
          title: `Leave a rating for ${label}`,
          body: `Delivery confirmed. Rate your buyer.`,
          link: `/orders/${t.buy_order_id}`,
          urgencyRank: 20,
          deadlineAt: null,
        });
      }
    }
    tasks.sort((a, b) => a.urgencyRank - b.urgencyRank);

    // Sales chart (last 31 days, released txns)
    const daily = new Map<string, number>();
    for (let i = 30; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 24 * 3600 * 1000);
      daily.set(d.toISOString().slice(0, 10), 0);
    }
    let earningsThisMonthCents = 0;
    let earningsLastMonthCents = 0;
    let releasedCount = 0;
    let releasedSum = 0;
    for (const t of txns) {
      if (t.status !== "released" && t.status !== "delivered") continue;
      const at = t.released_at ?? t.delivered_at ?? t.created_at;
      if (!at) continue;
      const day = at.slice(0, 10);
      if (daily.has(day)) daily.set(day, (daily.get(day) ?? 0) + (t.amount_cents ?? 0));
      if (at >= monthStart) earningsThisMonthCents += t.amount_cents ?? 0;
      else if (at >= lastMonthStart && at < monthStart) earningsLastMonthCents += t.amount_cents ?? 0;
      releasedCount++;
      releasedSum += t.amount_cents ?? 0;
    }
    const sales31d: SellerHubSalesPoint[] = Array.from(daily.entries()).map(([date, cents]) => ({ date, cents }));

    // Recent ratings (30d)
    const { count: recent30d } = await supabase
      .from("ratings")
      .select("id", { count: "exact", head: true })
      .eq("ratee_id", userId)
      .gte("created_at", thirtyAgo);

    return {
      metrics: {
        activeOffers,
        awaitingPhotos,
        awaitingShipment,
        completedThisMonth,
        totalOffersSubmitted: offers.length,
      },
      tasks: tasks.slice(0, 12),
      sales31d,
      earningsThisMonthCents,
      earningsLastMonthCents,
      avgOrderCents: releasedCount > 0 ? Math.round(releasedSum / releasedCount) : 0,
      feedback: {
        ratingAvg: Number(profileRes.data?.seller_rating_avg ?? 0),
        ratingCount: profileRes.data?.seller_rating_count ?? 0,
        recent30d: recent30d ?? 0,
      },
    };
  });

export const listSellerHubOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ orders: SellerHubOrderRow[] }> => {
    const { supabase, userId } = context;
    const { data: offers, error } = await supabase
      .from("offers")
      .select("id, status, price_cents, currency, buy_order_id, created_at, buy_orders(card_name, title, buyer_id)")
      .eq("seller_id", userId)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);

    const offerIds = (offers ?? []).map((o) => o.id);
    const buyerIds = Array.from(
      new Set(
        (offers ?? [])
          .map((o) => (o as { buy_orders?: { buyer_id?: string | null } }).buy_orders?.buyer_id ?? null)
          .filter((v): v is string => !!v),
      ),
    );

    const [txnsRes, buyersRes] = await Promise.all([
      offerIds.length
        ? supabase
            .from("transactions")
            .select("id, offer_id, status, shipping_cents, shipped_at, released_at, delivered_at, created_at")
            .in("offer_id", offerIds)
        : Promise.resolve({ data: [] as Array<{ id: string; offer_id: string | null; status: string; shipping_cents: number | null; shipped_at: string | null; released_at: string | null; delivered_at: string | null; created_at: string }>, error: null }),
      buyerIds.length
        ? supabase
            .from("profiles")
            .select("id, username, buyer_rating_avg")
            .in("id", buyerIds)
        : Promise.resolve({ data: [] as Array<{ id: string; username: string | null; buyer_rating_avg: number | null }>, error: null }),
    ]);
    const txnByOffer = new Map<string, {
      id: string;
      offer_id: string | null;
      status: string;
      shipping_cents: number | null;
      shipped_at: string | null;
      released_at: string | null;
      delivered_at: string | null;
      created_at: string;
    }>();
    for (const t of txnsRes.data ?? []) if (t.offer_id) txnByOffer.set(t.offer_id, t);

    const buyerById = new Map<string, { username: string | null; buyer_rating_avg: number | null }>();
    for (const b of buyersRes.data ?? []) buyerById.set(b.id, { username: b.username, buyer_rating_avg: b.buyer_rating_avg });

    const rows: SellerHubOrderRow[] = (offers ?? []).map((o) => {
      const bo = (o as { buy_orders?: { card_name?: string | null; title?: string | null; buyer_id?: string | null } }).buy_orders ?? {};
      const t = txnByOffer.get(o.id) ?? null;
      const buyer = bo.buyer_id ? buyerById.get(bo.buyer_id) : undefined;
      const updatedAt = t?.shipped_at ?? t?.released_at ?? t?.delivered_at ?? t?.created_at ?? o.created_at;
      return {
        offerId: o.id,
        buyOrderId: o.buy_order_id,
        transactionId: t?.id ?? null,
        cardName: bo.card_name ?? bo.title ?? null,
        buyerId: bo.buyer_id ?? null,
        buyerUsername: buyer?.username ?? null,
        buyerRatingAvg: buyer?.buyer_rating_avg != null ? Number(buyer.buyer_rating_avg) : null,
        priceCents: o.price_cents,
        shippingCents: t?.shipping_cents ?? 0,
        currency: o.currency,
        offerStatus: o.status,
        txnStatus: t?.status ?? null,
        updatedAt,
        createdAt: o.created_at,
        bucket: classifyBucket(o.status, t?.status ?? null),
      };
    });

    return { orders: rows };
  });

export const getMyOfferCount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ count: number }> => {
    const { supabase, userId } = context;
    const { count } = await supabase
      .from("offers")
      .select("id", { count: "exact", head: true })
      .eq("seller_id", userId);
    return { count: count ?? 0 };
  });

export type SellerHubPerformance = {
  acceptanceRatePct: number;
  avgPhotoHours: number | null;
  avgShipHours: number | null;
  disputeRatePct: number;
  totalOffers: number;
  totalAccepted: number;
  totalCompleted: number;
  totalDisputes: number;
  ratings: { avg: number; total: number; breakdown: Record<1 | 2 | 3 | 4 | 5, number> };
  benchmark: {
    acceptanceRatePct: number;
    avgPhotoHours: number;
    avgShipHours: number;
    disputeRatePct: number;
    ratingAvg: number;
  };
};

// Static-ish platform benchmarks. Kept as constants until we have enough data
// to compute rolling platform averages safely.
const PLATFORM_BENCHMARK = {
  acceptanceRatePct: 62,
  avgPhotoHours: 18,
  avgShipHours: 30,
  disputeRatePct: 1.8,
  ratingAvg: 4.7,
} as const;

function hoursBetween(a: string | null, b: string | null): number | null {
  if (!a || !b) return null;
  return (new Date(b).getTime() - new Date(a).getTime()) / 3_600_000;
}

export const getSellerHubPerformance = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SellerHubPerformance> => {
    const { supabase, userId } = context;

    const [offersRes, txnsRes, ratingsRes] = await Promise.all([
      supabase
        .from("offers")
        .select("id, status, created_at, updated_at")
        .eq("seller_id", userId),
      supabase
        .from("transactions")
        .select("id, status, created_at, shipped_at, released_at, delivered_at")
        .eq("seller_id", userId),
      supabase
        .from("ratings")
        .select("score")
        .eq("ratee_id", userId),
    ]);

    const offers = offersRes.data ?? [];
    const txns = txnsRes.data ?? [];
    const ratings = ratingsRes.data ?? [];

    const totalOffers = offers.length;
    const acceptedStatuses = new Set([
      "accepted",
      "pending_buyer_confirmation",
      "buyer_confirmed",
      "completed",
      "fulfilled",
    ]);
    const totalAccepted = offers.filter((o) => acceptedStatuses.has(o.status)).length;

    // Photo submission time: offer created_at → updated_at once seller
    // uploaded timestamped photos (status = pending_buyer_confirmation).
    const photoHours: number[] = [];
    for (const o of offers) {
      if (o.status !== "pending_buyer_confirmation") continue;
      const h = hoursBetween(o.created_at, o.updated_at);
      if (h !== null && h >= 0 && h < 24 * 30) photoHours.push(h);
    }

    // Ship time: transaction created_at → shipped_at
    const shipHours: number[] = [];
    for (const t of txns) {
      const h = hoursBetween(t.created_at, t.shipped_at);
      if (h !== null && h >= 0 && h < 24 * 30) shipHours.push(h);
    }

    const completedStatuses = new Set(["released", "delivered", "shipped", "funds_held"]);
    const totalCompleted = txns.filter((t) => completedStatuses.has(t.status)).length;

    // Disputes on this seller's transactions
    const txnIds = txns.map((t) => t.id);
    let totalDisputes = 0;
    if (txnIds.length) {
      const { count } = await supabase
        .from("disputes")
        .select("id", { count: "exact", head: true })
        .in("transaction_id", txnIds);
      totalDisputes = count ?? 0;
    }

    const breakdown: Record<1 | 2 | 3 | 4 | 5, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;
    for (const r of ratings) {
      const s = Math.min(5, Math.max(1, Math.round(r.score))) as 1 | 2 | 3 | 4 | 5;
      breakdown[s]++;
      sum += r.score;
    }
    const ratingAvg = ratings.length ? sum / ratings.length : 0;

    const avg = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

    return {
      acceptanceRatePct: totalOffers ? Math.round((totalAccepted / totalOffers) * 1000) / 10 : 0,
      avgPhotoHours: avg(photoHours) !== null ? Math.round(avg(photoHours)! * 10) / 10 : null,
      avgShipHours: avg(shipHours) !== null ? Math.round(avg(shipHours)! * 10) / 10 : null,
      disputeRatePct: totalCompleted ? Math.round((totalDisputes / totalCompleted) * 1000) / 10 : 0,
      totalOffers,
      totalAccepted,
      totalCompleted,
      totalDisputes,
      ratings: { avg: Math.round(ratingAvg * 100) / 100, total: ratings.length, breakdown },
      benchmark: { ...PLATFORM_BENCHMARK },
    };
  });
